import * as moment from 'moment';
import * as _ from 'lodash';
import React, { useState, useEffect, useContext } from 'react';
import { Dispatch, connect } from 'react-redux';
import { Alert, Button, Glyphicon, Panel, PanelGroup } from 'react-bootstrap';
import { FileDocumentType } from '@nkr/enums/document-type';
import { downloadFile } from '@nkr/services/download-file';
import { toast } from 'react-toastify';
import { AppActionCreators, IApplicationState, IPatientIntakeResponse, RecipientActionCreators } from '@nkr/store/index';
import { PatientIntakeTabs } from './nav';
import { IDetailData } from './detail-data';
import { useForm, FormProvider } from 'react-hook-form';
import { Utility } from '@nkr/services/utility';
import { PatientIntakeContext } from './context';
import RegistrationDetails from './details/registration-details';
import PersonalInformation from './details/personal-information';
import ContactInformation from './details/contact-information';
import Address from './details/address';
import ReferralDetails from './details/referral-details';
import Documents from './details/documents';
import SubmitTransferRequest from './details/transfer-request/submit';
import CompleteTransferRequest from './details/transfer-request/complete';

interface IStateProps {
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getPatientIntake: (medicalRecordId: number, centerId: number) => Promise<IPatientIntakeResponse>,
}

interface IProps { }

type TProps = IStateProps & IDispatchProps & IProps;

const Details = (props: TProps) => {
    const context = useContext(PatientIntakeContext);

    const [isSaving, setIsSaving] = useState(false);
    const [phoneNumbersNormalized, setPhoneNumbersNormalized] = useState(false);

    const [previousStatus, setPreviousStatus] = useState(0);

    const methods = useForm<IDetailData>({ mode: 'onSubmit' });

    const { handleSubmit, reset, setValue, watch, formState, formState: { errors, isDirty, isValid, isSubmitSuccessful } } = methods;

    const formValues = watch();

    const [lastSelectedState, setLastSelectedState] = useState<string>('');

    useEffect(() => {
        if (context.medicalRecordId > 0) {
            loadData();
        }
    }, [context.medicalRecordId])

    useEffect(() => {
        updateStateProvince(formValues?.addressIsUs, formValues?.stateProvince);
    }, [formValues?.stateProvince, formValues?.addressIsUs, context.medicalRecordId]);

    useEffect(() => {
        if (phoneNumbersNormalized) return;

        if (formValues.phoneNumber) {
            let normalizedPhone = Utility.normalizePhoneNumber(formValues.phoneNumber);

            if (normalizedPhone !== formValues.phoneNumber) {
                setValue('phoneNumber', normalizedPhone);
            }
        }

        if (formValues.phoneNumberSecondary) {
            let normalizedSecondaryPhone = Utility.normalizePhoneNumber(formValues.phoneNumberSecondary);

            if (normalizedSecondaryPhone !== formValues.phoneNumberSecondary) {
                setValue('phoneNumberSecondary', normalizedSecondaryPhone);
            }
        }

        setPhoneNumbersNormalized(true);
    }, [formValues?.phoneNumber, formValues?.phoneNumberSecondary, phoneNumbersNormalized]);

    useEffect(() => {
        if (isSubmitSuccessful) {
            reset({ ...formValues });
        }
    }, [formState, reset])

    const updateStateProvince = (addressIsUs: number, stateProvince: any) => {
        if (addressIsUs === 1) {
            if (stateProvince) {
                if (typeof stateProvince === 'number') {
                    setValue('stateProvinceValue', stateProvince.toString());
                    setLastSelectedState(stateProvince.toString());
                } else if (typeof stateProvince === 'string') {
                    let selectedState = context.stateList.find(s => s.abbreviation === stateProvince);

                    if (selectedState) {
                        setValue('stateProvinceValue', selectedState.id.toString());
                        setLastSelectedState(selectedState.id.toString());
                    } else {
                        setValue('stateProvinceValue', '');
                    }
                }
            } else if (lastSelectedState) {
                setValue('stateProvinceValue', lastSelectedState);
            } else {
                setValue('stateProvinceValue', '');
            }
        } else {
            if (typeof stateProvince === 'string') {
                setValue('stateProvinceValue', stateProvince || '');
            } else if (typeof stateProvince === 'number') {
                setValue('stateProvinceValue', '');
            } else {
                setValue('stateProvinceValue', '');
            }
        }
    }

    const loadData = async () => {
        reset();

        await props.showLoading('Retrieving data, one moment pleaese...');

        let response = await props.getPatientIntake(context.medicalRecordId, context.centerId);

        if (response.data.success) {
            let data = response.data.results;

            if (data.addressIsUs === 1) {
                let selectedState = context.stateList.find(s => s.abbreviation === data.stateProvince);

                if (selectedState) {
                    setLastSelectedState(selectedState.id.toString());
                }
            }

            setPreviousStatus(data.referralStatusId);

            Object.keys(data).map(key => {
                setValue(key as any, data[key]);
            });

            updateStateProvince(data.addressIsUs, data.stateProvince);

            if (data.transferRequest) {
                setValue('approveTransfer', -1);
            }
        }

        props.hideLoading();
    };

    const handleSave = async () => {
        try {
            if (!isValid) {
                toast.error('Please correct the validation errors');

                let el = document.getElementsByClassName('rc-drawer-content');

                if (el.length > 0) {
                    el[0].scrollTo(0, 0);
                }

                return;
            }

            setIsSaving(true);

            await context.onSave(formValues, formValues.createRecipient, formValues.createMicrosite);
        } catch (error) {
            let errorMessage = error.response?.data || 'Failed to update patient details';
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    const getAlertStyle = () => {
        let nextFollowUpDate = moment(formValues.followUpDate, 'MM/DD/YYYY', true);

        let nextFollowUpDateIsPast = nextFollowUpDate.isValid() ? nextFollowUpDate.isBefore(moment(), 'day') : false;
        let nextFollowUpDateIsToday = nextFollowUpDate.isValid() ? nextFollowUpDate.isSame(moment(), 'day') : false;

        if (nextFollowUpDateIsPast) {
            return { level: 3, style: 'danger' };
        }

        if (nextFollowUpDateIsToday) {
            return { level: 2, style: 'warning' };
        }

        return null;
    };

    const handleFileDownload = () => {
        context.onDownloadFile(FileDocumentType.PsAboTyping, formValues.patientScreenId)
            .then((response: any) => {
                let fileName = formValues.firstName + '_' + formValues.lastName
                    + '_' + FileDocumentType[FileDocumentType.PsAboTyping].toString()
                    + '_' + moment().format('YYYY_MM_DD');

                downloadFile(response.data, fileName, response.data.type);
            });
    }

    const handleGetQuestionaireReport = () => {
        // props.onGetQuestionaireReport(data.medicalRecordId)
        //     .then((response: any) => {
        //         let fileName = data.firstName + '_' + data.lastName
        //             + '_Questionnaire' +
        //             + '_' + moment().format('YYYY_MM_DD');

        //         downloadFile(response.data, fileName, response.data.type);
        //     });
    }

    const handleTransferRequestUpdate = (approved: boolean) => {
        context.onRefreshList();
        context.onCancelView();
    };

    const handleTransferRequestSubmitted = () => {
        context.onRefreshList();
        context.onCancelView();
    }

    if (formState == null) {
        return null;
    }

    const availableCoordinators = context.isAdminView && formValues.centerId && formValues.centerId > 0
        ? context.coordinatorList.filter(c => (c as any).centerId === formValues.centerId)
        : context.coordinatorList;

    const alertStyle = getAlertStyle();

    const hasAboResults = formValues.hasAboResults;

    const disableEdit = context.isAdminView == false && formValues.allowCenterUpdate == false || context.currentTab == PatientIntakeTabs.PENDING_ORDERS;
    const transferRequest = formValues.transferRequest;

    return (
        <>
            <div style={{ padding: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <h3 style={{ margin: 0 }}>Patient Intake Registration</h3>
                    {isDirty && <div style={{ color: 'forestgreen', backgroundColor: '#ddd', padding: '2px 4px', borderRadius: 4 }}>One or more fields have changed</div>}
                    <Button bsStyle='link' bsSize='sm' onClick={context.onCancelView}><Glyphicon glyph='remove' /></Button>
                </div>

                {isDirty && !isValid && (
                    <Alert bsStyle='danger'>
                        <h4>Please correct the following errors:</h4>
                        <ul>
                            {Object.values(errors).filter(v => v.message != '').map((v, index) => (
                                <li key={'v=' + index}>{v.message}</li>
                            ))}
                        </ul>
                    </Alert>
                )}

                <PanelGroup>
                    <FormProvider {...methods}>
                        <form onSubmit={handleSubmit(handleSave)}>
                            {context.showTransfer && (
                                <SubmitTransferRequest
                                    medicalRecordId={context.medicalRecordId}
                                    medicalRecordCenterId={formValues?.centerId ?? 0}
                                    firstName={formValues.firstName}
                                    lastName={formValues.lastName}
                                    micrositeRecordStatus={formValues.micrositeRecordStatus}
                                    recipientRecordStatus={formValues.recipientRecordStatus}
                                    onFinished={handleTransferRequestSubmitted}
                                    onCancel={() => context.onToggleTransfer(context.medicalRecordId, false)}
                                />
                            )}

                            {transferRequest == null ?
                                <RegistrationDetails />
                                :
                                <CompleteTransferRequest onUpdate={handleTransferRequestUpdate} />
                            }

                            <PersonalInformation
                                disableEdit={disableEdit}
                            />

                            <ContactInformation
                                disableEdit={disableEdit}
                            />

                            <Address
                                disableEdit={disableEdit}
                            />

                            <ReferralDetails
                                disableEdit={disableEdit}
                                isAdminView={context.isAdminView}
                                alertStyle={alertStyle}
                                previousStatus={previousStatus}
                                referralStatusList={context.referralStatusList}
                                declineReasonList={context.declineReasonList}
                                availableCoordinators={availableCoordinators}
                            />

                            <Documents
                                hasAboResults={hasAboResults}
                                onDownloadFile={handleFileDownload}
                                onGetQuestionaireReport={handleGetQuestionaireReport}
                            />

                            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 15 }}>
                                <Button
                                    type='submit'
                                    bsStyle='primary'
                                    disabled={isSaving || !isValid || disableEdit}
                                >
                                    {isSaving ? 'Updating...' : 'Update Record'}
                                </Button>
                            </div>
                        </form>
                    </FormProvider>
                </PanelGroup>
            </div>
        </>
    );
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        // app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getPatientIntake: (medicalRecordId, centerId) => {
            return dispatch(RecipientActionCreators.getPatientIntake(medicalRecordId, centerId));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(Details);